import { useEffect, useMemo, useRef, useState } from 'react';
import { CONFIG } from '../config';
import { decodeOne, predecode } from '../lib/decode';
import { useContent } from '../ui/ContentContext';
import { SmartImg } from '../ui/SmartImg';

interface Layer {
  n: number;
  slide: number;
}

/**
 * Modo reposo: slideshow con Ken Burns. Solo hay 2 capas montadas (la saliente y la entrante) y
 * solo se animan transform/opacity. Cada imagen se decodifica ANTES de entrar, así el fundido
 * nunca muestra una imagen a medio cargar.
 */
export function Attract({ onStart }: { onStart: () => void }) {
  const { content } = useContent();
  const { manifest, src } = content;
  const slides = useMemo(() => manifest.reposo.filter((s) => !!src(s.file)), [manifest, src]);
  const [layers, setLayers] = useState<Layer[]>(() => [{ n: 0, slide: 0 }]);
  const counter = useRef(0);

  useEffect(() => {
    counter.current = 0;
    setLayers([{ n: 0, slide: 0 }]);
    if (slides.length < 2) return;
    let alive = true;
    let timer: ReturnType<typeof setTimeout>;
    const advance = async () => {
      let nextN = counter.current + 1;
      // Una imagen que no decodifica (archivo dañado o faltante) se salta; un timeout no
      // bloquea el slideshow: la capa entra con su LQIP y la imagen aparece al terminar
      for (let tries = 0; tries < slides.length; tries++, nextN++) {
        const r = await decodeOne(src(slides[nextN % slides.length].file));
        if (!alive) return;
        if (r !== 'error') break;
      }
      const slide = nextN % slides.length;
      counter.current = nextN;
      setLayers((ls) => [...ls.slice(-1), { n: nextN, slide }]);
      timer = setTimeout(advance, CONFIG.slideMs);
    };
    timer = setTimeout(advance, CONFIG.slideMs);
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [slides, src]);

  // En reposo hay tiempo libre: deja decodificadas las portadas de la grilla de proyectos
  useEffect(() => {
    const t = setTimeout(() => predecode(manifest.projects.map((p) => src(p.cover.card))), 2500);
    return () => clearTimeout(t);
  }, [manifest, src]);

  const current = layers[layers.length - 1];
  const cur = slides[current.slide % Math.max(1, slides.length)];
  const project = cur?.projectId ? manifest.projects.find((p) => p.id === cur.projectId) : null;

  return (
    <div className="screen overflow-hidden bg-primario" onClick={onStart} data-screen="reposo">
      {slides.length === 0 ? null : (
        layers.map((l, i) => {
          const s = slides[l.slide % slides.length];
          return (
            <div
              key={l.n}
              className="fill"
              style={{ animation: i === layers.length - 1 && l.n > 0 ? `tkFade ${CONFIG.fadeMs}ms ease both` : undefined }}
            >
              <SmartImg
                file={s.file}
                lqip={s.lqip}
                className="fill"
                imgStyle={{ animation: `kb${l.n % 4} ${CONFIG.slideMs + CONFIG.fadeMs * 2}ms linear both`, willChange: 'transform' }}
              />
            </div>
          );
        })
      )}

      {/* Velos de legibilidad (degradados estáticos: sin blur, baratos para la GPU) */}
      <div className="fill pointer-events-none" style={{ background: 'linear-gradient(180deg, rgba(28,41,76,0.85) 0%, rgba(28,41,76,0) 26%)' }} />
      <div className="fill pointer-events-none" style={{ background: 'linear-gradient(0deg, rgba(24,35,63,0.96) 0%, rgba(24,35,63,0.75) 30%, rgba(24,35,63,0) 58%)' }} />

      <img src="/brand/logo-ontario-blanco.png" alt="Ontario" className="a-fade absolute" style={{ left: 64, top: 88, height: 112 }} draggable={false} />

      <div className="absolute flex flex-col" style={{ left: 64, right: 64, top: 1060, gap: 24 }}>
        <div className="a-left rounded bg-secundario" style={{ width: 120, height: 8 }} />
        <div className="a-up font-bold text-blanco" style={{ fontSize: 84, lineHeight: 1.04, letterSpacing: -1 }}>
          Descubre nuestros proyectos
        </div>
        <div className="font-semibold text-blanco/80" style={{ fontSize: 30, minHeight: 40 }}>
          {project ? [project.nombre, project.ubicacion].filter(Boolean).join(' · ') : ' '}
        </div>
      </div>

      <div className="absolute flex justify-center" style={{ left: 0, right: 0, top: 1400 }}>
        <div className="relative">
          <div className="fill rounded-full border-4 border-secundario" style={{ animation: 'ringOut 2.4s ease-out infinite' }} />
          <div
            className="relative flex items-center rounded-full bg-secundario font-bold text-primario"
            style={{ height: 112, padding: '0 56px', fontSize: 34, gap: 18, animation: 'ctaPulse 2.4s ease-in-out infinite' }}
          >
            <TapIcon />
            Toca la pantalla para comenzar
          </div>
        </div>
      </div>
    </div>
  );
}

function TapIcon() {
  return (
    <svg width="44" height="44" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M9 11V5.5a1.5 1.5 0 0 1 3 0V11m0-1.5a1.5 1.5 0 0 1 3 0V12m0-1a1.5 1.5 0 0 1 3 0v3.5c0 3.6-2.4 6.5-6 6.5-2.4 0-3.6-1-5-3l-2.6-4a1.4 1.4 0 0 1 2.2-1.7L9 14"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
